import { useEffect } from 'react';
import type { ReactNode } from 'react';
import { Caption } from './primitives';
import { Sparkline } from './telemetry';

/**
 * Modals partagées entre Mission Control et Parent Dashboard. Single source
 * of truth — les deux pages consomment exactement les mêmes composants pour
 * la drill-down session + phase.
 */

// ─── Modal base ────────────────────────────────────────────────────────────

export function Modal({
  open,
  onClose,
  children,
  ariaLabel,
  maxWidth = 560,
}: {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  ariaLabel?: string;
  maxWidth?: number;
}): JSX.Element | null {
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div
      onClick={onClose}
      role="dialog"
      aria-label={ariaLabel}
      aria-modal="true"
      className="fixed inset-0 z-50 bg-ink-900/70 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-ink-800 border border-ink-700 rounded-xl p-6 w-full relative animate-card-enter"
        style={{ maxWidth }}
      >
        <button
          onClick={onClose}
          aria-label="Fermer"
          className="absolute top-3 right-3 text-paper-50 hover:text-coral-500 transition-all duration-150 text-xl leading-none"
        >
          ✕
        </button>
        {children}
      </div>
    </div>
  );
}

// ─── SessionDetailModal ────────────────────────────────────────────────────

export interface SessionDetail {
  id: string;
  date: string;
  start_time: string;
  duration_min: number;
  attempts: number;
  success: number;
  modules: string[];
  avg_sec: number;
  attempt_timeline?: number[]; // attempts par tranche de 5min pour rate-of-fire
}

export function SessionDetailModal({
  session,
  onClose,
}: {
  session: SessionDetail | null;
  onClose: () => void;
}): JSX.Element | null {
  if (!session) return null;
  const successRate = session.attempts > 0 ? Math.round((session.success / session.attempts) * 100) : 0;
  const moduleLabels: Record<string, string> = {
    opening: 'Ouverture',
    tactic: 'Tactiques',
    endgame: 'Finale',
    autopsy: 'Autopsie',
  };
  return (
    <Modal open onClose={onClose} ariaLabel={`Session ${session.id}`} maxWidth={520}>
      <Caption color="paper">Session · {session.id}</Caption>
      <h2 className="font-display text-heading-2 text-paper-50 mt-1 mb-4">
        {session.date} · {session.start_time}
      </h2>

      <div className="grid grid-cols-4 gap-3 mb-4">
        <div className="bg-ink-900 border border-ink-700 rounded-lg p-2.5">
          <div className="text-[10px] text-ink-300 uppercase tracking-wider">Durée</div>
          <div className="font-mono text-paper-50 tabular-nums">{session.duration_min} min</div>
        </div>
        <div className="bg-ink-900 border border-ink-700 rounded-lg p-2.5">
          <div className="text-[10px] text-ink-300 uppercase tracking-wider">Tentatives</div>
          <div className="font-mono text-paper-50 tabular-nums">{session.attempts}</div>
        </div>
        <div className="bg-ink-900 border border-ink-700 rounded-lg p-2.5">
          <div className="text-[10px] text-ink-300 uppercase tracking-wider">Réussite</div>
          <div className="font-mono text-volt-500 tabular-nums">{successRate}%</div>
        </div>
        <div className="bg-ink-900 border border-ink-700 rounded-lg p-2.5">
          <div className="text-[10px] text-ink-300 uppercase tracking-wider">Moy/coup</div>
          <div className="font-mono text-paper-50 tabular-nums">{session.avg_sec}s</div>
        </div>
      </div>

      <div className="mb-4">
        <Caption>Modules touchés</Caption>
        <div className="flex flex-wrap gap-2 mt-1.5">
          {session.modules.map((m) => (
            <span
              key={m}
              className="px-2 py-1 rounded-md bg-ink-700 text-paper-50 text-xs uppercase tracking-wider font-mono"
            >
              {moduleLabels[m] ?? m}
            </span>
          ))}
        </div>
      </div>

      {session.attempt_timeline && session.attempt_timeline.length >= 2 && (
        <div>
          <Caption>Cadence (tentatives par 5 min)</Caption>
          <div className="mt-2">
            <Sparkline data={session.attempt_timeline} width={460} height={48} color="var(--acid-500)" />
          </div>
        </div>
      )}
    </Modal>
  );
}

// ─── PhaseDetailModal ──────────────────────────────────────────────────────

export interface PhaseDetail {
  phase: 'opening' | 'middlegame' | 'endgame';
  total: number;
  recent_mistakes?: Array<{ ply: number; eval_drop_cp: number; game_id: number; played_at?: string }>;
}

export function PhaseDetailModal({
  phase,
  onClose,
}: {
  phase: PhaseDetail | null;
  onClose: () => void;
}): JSX.Element | null {
  if (!phase) return null;
  const phaseLabels: Record<PhaseDetail['phase'], string> = {
    opening: 'Ouverture',
    middlegame: 'Milieu de partie',
    endgame: 'Finale',
  };
  const phaseColors: Record<PhaseDetail['phase'], string> = {
    opening: 'var(--violet-500)',
    middlegame: 'var(--cyan-500)',
    endgame: 'var(--amber-500)',
  };
  return (
    <Modal open onClose={onClose} ariaLabel={`Phase ${phase.phase}`} maxWidth={560}>
      <Caption color="paper">Phase · {phaseLabels[phase.phase]}</Caption>
      <h2 className="font-display text-heading-2 text-paper-50 mt-1 mb-1">
        {phase.total} <span className="text-base text-ink-300 italic">erreurs · 30j</span>
      </h2>
      <div
        className="h-1 rounded-full mt-3 mb-5"
        style={{ background: phaseColors[phase.phase] }}
      />

      {phase.recent_mistakes && phase.recent_mistakes.length > 0 ? (
        <div className="flex flex-col gap-2">
          <Caption>5 dernières erreurs</Caption>
          {phase.recent_mistakes.slice(0, 5).map((m, i) => (
            <div
              key={i}
              className="flex items-center justify-between bg-ink-900 border border-ink-700 rounded-md px-3 py-2 font-mono text-sm"
            >
              <span className="text-ink-300 tabular-nums">coup {m.ply}</span>
              <span className="text-coral-500 tabular-nums">-{m.eval_drop_cp}cp</span>
              <span className="text-paper-50">partie n°{m.game_id}</span>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-ink-300 text-sm italic">
          Aucune erreur récente dans cette phase. (Détails à venir.)
        </p>
      )}
    </Modal>
  );
}
